'use client';

import { Tab, TabProps } from '@virginmediao2/momentum-nextjs/tabs';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import React, { FC, useCallback, useMemo } from 'react';

import { TABS_PERSIST_KEY } from './tabs-provider';
import { useTabsContext } from './tabs.context';

export interface TabProviderProps
  extends Omit<
    TabProps,
    'onClick' | 'active' | 'aria-controls' | 'role' | 'tabIndex'
  > {
  id: string;
  index: number;
  width: 'full-width' | 'default';
  onClick?: (
    event:
      | React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>
      | React.KeyboardEvent<HTMLElement>,
    tabId: string
  ) => void;
}

export const TabProvider: FC<TabProviderProps> = React.memo(
  ({ id, index, children, onClick, icon, ...props }) => {
    const { activeTabId, setActiveTabId, tabItems, tabRefs, persist } =
      useTabsContext();
    const pathname = usePathname();
    const router = useRouter();
    const searchParams = useSearchParams();
    const isActive = activeTabId === id;

    // Preserve existing URL parameters when creating href
    const href = useMemo(() => {
      if (!persist) return undefined;

      // Use searchParams from Next.js instead of window.location.search
      const params = new URLSearchParams(searchParams?.toString() || '');
      params.set(TABS_PERSIST_KEY, id);
      return `${pathname}?${params.toString()}`;
    }, [persist, pathname, id, searchParams]);

    const handleAnchorClick = useCallback(
      (e: React.MouseEvent<HTMLAnchorElement>) => {
        // Allow normal browser behavior for modified clicks
        if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) {
          return;
        }

        if (persist && href) {
          e.preventDefault();
          // Use Next.js router for smoother client-side navigation
          router.push(href, { scroll: false });
        }

        setActiveTabId(id);
        onClick?.(e, id);
      },
      [id, setActiveTabId, persist, onClick, href, router]
    );

    const handleTabClick = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        setActiveTabId(id);
        onClick?.(e, id);
      },
      [id, setActiveTabId, onClick]
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLElement>) => {
        const currentIndex = tabItems.findIndex(
          (item) => item._uid === activeTabId
        );
        let newIndex = currentIndex;

        // Handle Key Events for accessibility
        switch (e.key) {
          case 'ArrowLeft':
            newIndex = (currentIndex - 1 + tabItems.length) % tabItems.length;
            setActiveTabId(tabItems[newIndex]._uid as string);
            tabRefs.current[newIndex]?.focus();
            break;
          case 'ArrowRight':
            newIndex = (currentIndex + 1) % tabItems.length;
            setActiveTabId(tabItems[newIndex]._uid as string);
            tabRefs.current[newIndex]?.focus();
            break;
          case 'Home':
            e.preventDefault();
            newIndex = 0;
            setActiveTabId(tabItems[newIndex]._uid as string);
            tabRefs.current[newIndex]?.focus();
            break;
          case 'End':
            e.preventDefault();
            newIndex = tabItems.length - 1;
            setActiveTabId(tabItems[newIndex]._uid as string);
            tabRefs.current[newIndex]?.focus();
            break;
          case 'Enter':
          case ' ':
            e.preventDefault();
            setActiveTabId(id);
            onClick?.(e, id);
            break;
          default:
            return;
        }
      },
      [id, tabItems, tabRefs, activeTabId, setActiveTabId, onClick]
    );

    // Common props for both anchor and button variants
    const commonProps = {
      role: 'tab' as const,
      'aria-selected': isActive,
      'aria-controls': id,
      tabIndex: isActive ? 0 : -1,
      'data-testid': `tab-${id}`,
      onKeyDown: handleKeyDown,
    };

    if (persist && href) {
      return (
        <Tab
          asChild
          {...props}
          active={isActive}
          icon={icon}
          ref={(el) => {
            if (el) {
              tabRefs.current[index] = el as HTMLElement;
            }
          }}
        >
          <a href={href} onClick={handleAnchorClick} {...commonProps}>
            {children}
          </a>
        </Tab>
      );
    }

    return (
      <Tab
        {...props}
        active={isActive}
        icon={icon}
        onClick={handleTabClick}
        ref={(el) => {
          tabRefs.current[index] = el as HTMLElement;
        }}
        {...commonProps}
      >
        {children}
      </Tab>
    );
  }
);

TabProvider.displayName = 'TabProvider';